/**
 * リポジトリを使用するためのReactフック
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { 
  IMapRepository, 
  INodeRepository, 
  IEdgeRepository, 
  ITranscriptRepository, 
  MapModel 
} from '@/repositories/interfaces';
import { getRepositoryFactory, RepositoryType } from '@/repositories/RepositoryFactory';
import { TopicNode } from '@/types/nodeTypes';
import { TopicEdge } from '@/types/edgeTypes';

/**
 * リポジトリアクセスのためのフック
 * @param repositoryType リポジトリの種類（デフォルトはファクトリの設定に従う）
 * @returns 各種リポジトリとユーティリティ関数
 */
export function useRepository(repositoryType?: RepositoryType) {
  const factory = useMemo(() => getRepositoryFactory(), []);
  
  // リポジトリタイプをオプションで切り替え
  useEffect(() => {
    if (repositoryType && factory.getRepositoryType() !== repositoryType) {
      factory.setRepositoryType(repositoryType);
    }
  }, [repositoryType, factory]);
  
  // 各リポジトリを取得
  const mapRepository = useMemo(() => factory.getMapRepository(), [factory]);
  const nodeRepository = useMemo(() => factory.getNodeRepository(), [factory]);
  const edgeRepository = useMemo(() => factory.getEdgeRepository(), [factory]);
  const transcriptRepository = useMemo(() => factory.getTranscriptRepository(), [factory]);
  
  /**
   * テスト用のデータインジェクション関数
   */
  const injectTestData = useMemo(() => ({
    /**
     * フルマップデータを注入
     */
    injectMapData: (mapData: MapModel) => {
      factory.injectMockData(mapData);
    },
    
    /**
     * ノードデータのみを注入
     */
    injectNodes: (mapId: string, nodes: TopicNode[]) => {
      mapRepository.getMap(mapId).then(map => {
        if (map) {
          mapRepository.updateMap(mapId, { nodes });
        }
      });
    },
    
    /**
     * エッジデータのみを注入
     */
    injectEdges: (mapId: string, edges: TopicEdge[]) => {
      mapRepository.getMap(mapId).then(map => {
        if (map) {
          mapRepository.updateMap(mapId, { edges });
        }
      });
    },
    
    /**
     * トランスクリプトを注入
     */
    injectTranscript: (mapId: string, transcript: string) => {
      transcriptRepository.updateTranscript(mapId, transcript);
    },
    
    /**
     * テストデータをリセット
     */
    resetTestData: () => {
      factory.resetMockRepositories();
    }
  }), [factory, mapRepository, transcriptRepository]);
  
  return {
    mapRepository,
    nodeRepository,
    edgeRepository,
    transcriptRepository,
    testUtils: injectTestData
  };
}

/**
 * マップデータのみを取得するシンプルなフック
 * @param mapId マップID
 */
export function useMapData(mapId: string) {
  const { mapRepository, nodeRepository, edgeRepository, transcriptRepository } = useRepository();
  const [map, setMap] = useState<MapModel | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  
  // マップデータをロード
  const loadMap = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await mapRepository.getMap(mapId);
      setMap(data);
    } catch (err) {
      console.error('Failed to load map:', err);
      setError(err instanceof Error ? err : new Error('Failed to load map'));
    } finally {
      setLoading(false);
    }
  }, [mapId, mapRepository]);
  
  // 初期ロード
  useEffect(() => {
    loadMap();
  }, [loadMap]);
  
  // ノード更新
  const updateNode = useCallback(async (nodeId: string, update: Partial<TopicNode>) => {
    try {
      const result = await nodeRepository.updateNode(mapId, nodeId, update);
      if (result && map) {
        // ローカル状態も更新
        const updatedNodes = map.nodes.map(node => 
          node.id === nodeId ? { ...node, ...update } : node
        );
        
        setMap({ ...map, nodes: updatedNodes });
      }
      return result;
    } catch (err) {
      console.error('Failed to update node:', err);
      throw err;
    }
  }, [mapId, map, nodeRepository]);
  
  // エッジ更新
  const updateEdge = useCallback(async (edgeId: string, update: Partial<TopicEdge>) => {
    try {
      const result = await edgeRepository.updateEdge(mapId, edgeId, update);
      if (result && map) {
        // ローカル状態も更新
        const updatedEdges = map.edges.map(edge => 
          edge.id === edgeId ? { ...edge, ...update } : edge
        );
        
        setMap({ ...map, edges: updatedEdges });
      }
      return result;
    } catch (err) {
      console.error('Failed to update edge:', err);
      throw err;
    }
  }, [mapId, map, edgeRepository]);
  
  // トランスクリプト更新
  const updateTranscript = useCallback(async (transcript: string) => {
    try {
      const result = await transcriptRepository.updateTranscript(mapId, transcript);
      if (result && map) {
        // ローカル状態も更新
        setMap({ ...map, transcript });
      }
      return result;
    } catch (err) {
      console.error('Failed to update transcript:', err);
      throw err;
    }
  }, [mapId, map, transcriptRepository]);
  
  // データ再ロード
  const refresh = useCallback(() => {
    loadMap();
  }, [loadMap]);
  
  return {
    map,
    loading,
    error,
    updateNode,
    updateEdge,
    updateTranscript,
    refresh
  };
}